/*
 *  (c) 2015 - 2024 ENISCO by FORCAM GmbH
 */

import React, {useState} from 'react'
import {Button, Dialog, DialogActions, Typography} from '@mui/material'
import {cloneSafe, TranslatedField, Translation, useError, useResolver, useTest} from '@enisco/mcc-react-lib'
import {FormattedMessage} from 'react-intl'
import {Favorite} from '../../../../../model/favorites'
import useLoadProfile from '../../../../../hooks/useLoadProfile'

interface Props {
  open: boolean
  onClose: () => void
  favorite: Favorite
}

const EditFavoriteDialog: React.FC<Props> = ({open, onClose, favorite}) => {
  const resolver = useResolver()
  const test = useTest()
  const error = useError()
  const [isNameDefined, setIsNameDefined] = useState(false)
  const [nameTranslations, setNameTranslations] = useState<Translation>()
  const [loadProfile] = useLoadProfile()

  const handleClose = () => {
    onClose()
  }

  const mockedData = {
    id: '1234abc',
    app: {app: 'app', module: 'module', hasAccess: true},
    user: 'testUser',
    groupId: 'abc1234'
  }

  const updateFavorite = () => {
    const favoriteToUpdate = cloneSafe(favorite) as any
    favoriteToUpdate.description = nameTranslations
    resolver('mcc-portal-api', '/internal/favorites/' + favorite.id, {
      options: {
        method: 'PUT',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(favoriteToUpdate)
      },
      mockedResponse: test ? JSON.stringify(mockedData) : undefined
    })
      .then(() => {
        handleClose()
        return loadProfile()
      })
      .catch(error)
  }

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby='my-favorite-dialog'
      open={open}
      PaperProps={{
        sx: {
          flexGrow: 1,
          paddingTop: 3,
          paddingLeft: 3,
          paddingRight: 3
        }
      }}
      data-testid={'EditFavoriteDialog:root'}
    >
      <Typography variant='h5'>
        {
          <FormattedMessage
            id='FavoriteDialog.EditFavorite'
            defaultMessage='Edit favorite'
          />
        }
      </Typography>
      <TranslatedField
        defaultValue={favorite.description}
        onValidChange={(valid: boolean) => setIsNameDefined(valid)}
        required
        name={
          <FormattedMessage
            id='FavoriteDialog.FavoriteName'
            defaultMessage='Favorite name'
          />
        }
        onChange={(data: Translation) => {
          setNameTranslations(data)
        }}
        value={nameTranslations}
      />
      <DialogActions sx={{paddingTop: 5}}>
        <Button onClick={handleClose}>
          <FormattedMessage
            id='FavoriteDialog.Cancel'
            defaultMessage='Cancel'
          />
        </Button>
        <Button disabled={!isNameDefined} onClick={updateFavorite}>
          <FormattedMessage id='FavoriteDialog.Ok' defaultMessage='Ok' />
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditFavoriteDialog
